import { IClause, IClauseList, IUser } from "@/api";
import CloseIcon from "@mui/icons-material/Close";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { DialogProps } from "@toolpad/core/useDialogs";
import { RecentUsage } from "./RecentUsage";
import { VersionHistory } from "./VersionHistory";
import { CustomGuidance } from "./guidance/CustomGuidance";
import { DefaultGuidance } from "./guidance/DefaultGuidance";

interface IProps {
    /**
     * The clause to display details for
     */
    clause: IClauseList | IClause;
    /**
     * The user viewing the clause details
     */
    user?: IUser | null;
}

/**
 * Displays the details of a clause
 */
export const ClauseDetailsDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const { clause, user } = payload;
    const theme = useTheme();
    const isListView = useMediaQuery(theme.breakpoints.down("md"));

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isListView}
            slotProps={{
                paper: {
                    sx: {
                        position: "fixed",
                        right: 0,
                        bottom: 0,
                        m: 0,
                        height: "100%",
                        maxHeight: "100vh",
                        borderRadius: 0,
                    },
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems={"start"}
                >
                    <Typography fontWeight={500} variant="h6">
                        {clause.title}
                    </Typography>
                    <div>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            edge="end"
                            sx={(theme) => ({
                                color: theme.palette.grey[500],
                            })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack gap={3}>
                    {/* Information */}
                    <Stack gap={1}>
                        <Typography fontWeight={500} gutterBottom>
                            Information
                        </Typography>
                        <Stack direction="row" gap={6}>
                            {/* Clause Types */}
                            <Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#6B7280" }}
                                    fontWeight={500}
                                >
                                    Clause Types
                                </Typography>
                                <Stack direction="row" gap={1}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                    >
                                        {clause.clause_types
                                            .map((ct) => ct.name)
                                            .join(", ")}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Policy Type */}
                            <Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#6B7280" }}
                                    fontWeight={500}
                                >
                                    Policy Type
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {clause.policy_type.name}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack direction="row" gap={6}>
                            {/* Created By */}
                            <Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#6B7280" }}
                                    fontWeight={500}
                                >
                                    Created By
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    InsureScope
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Guidance */}
                    <CustomGuidance clause={clause} />

                    {/* Default Guidance */}
                    <DefaultGuidance clause={clause} />

                    {/* Recent Usage */}
                    <RecentUsage clause={clause} onClose={handleClose} />

                    {/* Version History */}
                    {user?.is_admin && <VersionHistory clause={clause} />}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
